import React, { Component, ReactNode } from "react";
import { WarningCircle } from "phosphor-react";

export type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
};

export type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
  }

  handleReload = () => window.location.reload();
  handleGoHome = () => window.location.assign("/");

  render() {
    const { hasError } = this.state;
    if (!hasError) return this.props.children;

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex flex-col items-center justify-center min-h-screen px-4 py-8 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 transition-colors"
      >
        <WarningCircle size={64} weight="duotone" className="mb-4 text-red-500 dark:text-red-400" aria-hidden="true" />
        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
        <p className="mb-6 text-center max-w-md">
          Sorry, an unexpected error occurred. Please try reloading the page or return to the home screen.
        </p>
        <div className="flex gap-3">
          <button
            onClick={this.handleReload}
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            Reload
          </button>
          <button
            onClick={this.handleGoHome}
            className="px-4 py-2 rounded bg-neutral-200 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 hover:bg-neutral-300 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }
}
